import { FetchingButton, FieldSet, PhoneField, phone as phoneSchema } from '@glade-ai/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { Controller, type UseFormProps, useForm } from 'react-hook-form';
import { parsePhoneNumber } from 'react-phone-number-input';
import z from 'zod';

import FieldError from '@/components/FieldError';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@/tsClient';

import requestOTPErrorMessage from '../helpers/requestOTPErrorMessage';

type Props = {
  onSuccess: (args: { phoneNumber: string; country: string; recipient: string; canSendViaEmail: boolean }) => void;
  initialPhoneNumber?: string;
};

type FormSchema = {
  phone: string;
};

const RequestOTP: React.FC<Props> = ({ initialPhoneNumber, onSuccess }) => {
  const router = useRouter();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { getData: requestOTP, fetchingState: userAuthorizeState } = useNoodleApi(tsClient.auth.requestOTP, {
    healthMonitor: { name: 'sign-in' },
    reportError: false,
    toastOnError: true,
  });

  const formConfig = useMemo(
    () =>
      ({
        defaultValues: { phone: initialPhoneNumber || '' },
        mode: 'onBlur',
        resolver: zodResolver(
          // simple schema using shared phone validator
          z.object({ phone: phoneSchema() }),
        ),
      }) satisfies UseFormProps<FormSchema>,
    [initialPhoneNumber],
  );

  const { control, handleSubmit } = useForm<FormSchema>(formConfig);

  const onSubmit = async (data: FormSchema): Promise<void> => {
    setSubmitError(null);
    const parsed = (() => {
      try {
        return parsePhoneNumber(data.phone || '');
      } catch (_e) {
        return null;
      }
    })();
    const derivedCountry = parsed?.country || 'US';
    const response = await requestOTP({ channel: 'sms', phoneNumber: data.phone });
    if (response.data) {
      onSuccess({
        canSendViaEmail: response.data.canRequestViaEmail,
        country: derivedCountry,
        phoneNumber: data.phone,
        recipient: response.data.recipient,
      });
    } else if (response.error) {
      const msg = requestOTPErrorMessage(response.error);
      if (msg === '__SUPPORT__') {
        router.push(getUrl(IDENTIFIERS.SUPPORT));
      } else {
        setSubmitError(msg);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FieldSet>
        <Controller
          name='phone'
          control={control}
          render={({ field, fieldState }) => <PhoneField field={field} fieldState={fieldState} />}
        />

        {submitError && <FieldError>Error: {submitError}</FieldError>}

        <FetchingButton
          variant='primary'
          size='lg'
          type='submit'
          fetching={userAuthorizeState.isFetching}
          className='mt-4 w-full'
        >
          Sign In
        </FetchingButton>
      </FieldSet>
    </form>
  );
};

export default RequestOTP;
